import { Anchor, Icon, SubjectNewsSVG } from "@newsela/angelou";

import { unstyledHeadingsCss } from "@/components/components.style";
import ContentCard from "@/components/ContentCard/";
import ContentCardV2 from "@/components/ContentCardV2";
import HorizontalScrollContainer from "@/components/HorizontalScrollContainer";
import useGetUserExperimentSegment from "@/features/useGetUserExperimentSegment";

import AILunaTag from "@/components/AIComponents/AILunaTag/AILunaTag";

import { CardViewmodelAndProps } from "@/components/ContentCard/ContentCard.interface";
import { ContentRowProps } from "../ContentRow.interface";

import {
  contentScrollableListCss,
  defaultHeadingCss,
  headerAndDescriptionContainerCss,
  headerDescriptionCss,
  headerIconCss,
  homepageDefaultRowContainerCss,
  linkCss,
  listItemCss,
  lunaContainerCss,
} from "./HomepageDefaultRow.styles";

// Removes reading skills from cards for experiment.
export const omitSkillsFromResults = (results: CardViewmodelAndProps[]) => {
  const newResults = [...results];
  newResults.forEach((card) => {
    if (card.metadata.content_type === "ARTICLE") {
      delete card.metadata.reading_skills;
    }
  });
  return newResults;
};

const HomepageDefaultRow = ({
  children,
  dataQaSelector = "",
  dataTestId = "",
  description,
  iconSvg,
  isLuna = false,
  title,
  url,
  newContentCard,
}: ContentRowProps) => {
  const [removeReadingSkillChips] = useGetUserExperimentSegment(
    "remove_reading_skills_from_cards",
  );

  const isExperimentRemoveReadingSkillChipsFromSearch =
    removeReadingSkillChips === "on";

  if (children.length === 0) {
    return null;
  }

  const contentItems = children.slice(0, 8);

  const sanitizedResults = isExperimentRemoveReadingSkillChipsFromSearch
    ? omitSkillsFromResults(contentItems)
    : contentItems;

  const ContentCardSwitcher = (props: CardViewmodelAndProps) =>
    newContentCard ? <ContentCardV2 {...props} /> : <ContentCard {...props} />;

  return (
    <section css={homepageDefaultRowContainerCss} data-testid={dataTestId}>
      <div css={headerAndDescriptionContainerCss}>
        <div data-part="row-header" css={defaultHeadingCss}>
          <Icon
            SvgComponent={iconSvg || SubjectNewsSVG}
            __cssFor={{ root: headerIconCss }}
          />
          <h2 css={unstyledHeadingsCss}>{title}</h2>
          {url ? (
            <Anchor href={url} __cssFor={linkCss}>
              View all
            </Anchor>
          ) : null}
          {isLuna ? (
            <div css={lunaContainerCss}>
              <AILunaTag />
            </div>
          ) : null}
        </div>

        <div css={headerDescriptionCss}>{description}</div>
      </div>
      <HorizontalScrollContainer
        ariaLabel={typeof title === "string" ? title : undefined}
      >
        <ul css={contentScrollableListCss}>
          {sanitizedResults.map((content, index) => (
            <li
              css={listItemCss}
              key={index}
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} of ${sanitizedResults.length}`}
            >
              <ContentCardSwitcher
                {...content}
                dataQaSelector={dataQaSelector}
              />
            </li>
          ))}
        </ul>
      </HorizontalScrollContainer>
    </section>
  );
};

export default HomepageDefaultRow;
